import { atom, useAtom } from "jotai";
import styled from "styled-components/macro";
import ErrorPage from "../pages/ErrorPage/ErrorPage";
import { IconButton, Paper, Typography } from "@mui/material";
import { Close, InfoOutlined } from "@mui/icons-material";
import { useKey } from "react-use";
import { useIsStorybook } from "utils/customHooks/storyHooks";

export const errorOverlayStateAtom = atom<{
  error: boolean;
  message: string | React.JSX.Element;
  overrideDefaultMessage?: string;
}>({
  error: false,
  message: "",
  overrideDefaultMessage: undefined,
});
/**
 * Component that displays specific explanations for known error types
 */
function ErrorExplainer({ message }: { message: string | React.JSX.Element }) {
  const messageStr = typeof message === "string" ? message : "";
  const hasTooManySegments = messageStr
    .toLowerCase()
    .includes("too many segments");

  if (!hasTooManySegments) {
    return null;
  }

  return (
    <ExplainerPaper elevation={2}>
      <div className="explainer-header">
        <InfoOutlined color="primary" />
        <Typography variant="subtitle1" fontWeight="bold" color="primary">
          Troubleshooting Guide
        </Typography>
      </div>
      <Typography variant="body2">
        <strong>Too Many Segments:</strong> detected excessive switching between
        idle/producing -- try modifying process-state-machine config by
        increasing downtime threshold or adding a debounce
      </Typography>
    </ExplainerPaper>
  );
}

export default function ErrorOverlay() {
  const [errorOverlayState, setErrorOverlayState] = useAtom(
    errorOverlayStateAtom
  );
  useKey("Escape", () => {
    setErrorOverlayState(prev => ({ ...prev, error: false }));
  });
  const isStorybook = useIsStorybook();
  return !isStorybook && errorOverlayState.error ? (
    <>
      <ErrorOverlayStyles>
        <div className="content">
          <ErrorPage error={errorOverlayState.message} />
          {typeof errorOverlayState.message === "string" && (
            <ErrorExplainer message={errorOverlayState.message} />
          )}
          <div className="btnClose">
            <IconButton
              onClick={() =>
                setErrorOverlayState(prev => ({ ...prev, error: false }))
              }
            >
              <Close />
            </IconButton>
          </div>
        </div>
      </ErrorOverlayStyles>
    </>
  ) : null;
}

const ExplainerPaper = styled(Paper)`
  margin: 16px;
  padding: 16px;
  border-left: 4px solid #2196f3;

  .explainer-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
`;

const ErrorOverlayStyles = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 999;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.6);
  .content {
    max-width: 600px;
    margin: auto;
    margin-top: 128px;
    position: relative;
    background: white;
    border-radius: 10px;
    border: 1px solid #e5e5e5;
    box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  }
  .btnClose {
    position: absolute;
    top: 0px;
    right: 0px;
  }
`;
